/* HS!/JPD BUILDER — PHASE 2 (build-from-scratch)
 * Selects among FINISHED, validated patterns; never generates one.
 * Fit adjustment is clamped to the per-cut tolerance lists carried in
 * the cut_config metafield (rendered into data-config). Measurement
 * entry maps to the nearest graded size + bounded adjustment, and
 * refuses anything outside the validated range.
 * Cart lines: cut variant (base) + fabric variant + optional hardware
 * set + embellishment add-ons — all linked by CUSTOMIZATION REF and
 * stamped with ENTITY (hard requirement).
 */
(function () {
  'use strict';

  function money(cents, format) {
    var amount = (cents / 100).toFixed(2);
    if (format && format.indexOf('{{') !== -1) {
      return format
        // Character classes, not backslash escapes — theme JS stays free of
        // backslash literals so GraphQL theme-file deploys never mangle them.
        // (hs-customizer.js was fixed for this long ago; this file was missed.)
        .replace(/[{][{][ ]*amount[ ]*[}][}]/, Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 }))
        .replace(/[{][{][ ]*amount_no_decimals[ ]*[}][}]/, Math.round(cents / 100).toLocaleString('en-US'));
    }
    return '$' + amount;
  }

  function initSection(root) {
    var cfgEl = root.querySelector('[data-hsb-config]');
    if (!cfgEl) return;
    var cfg = JSON.parse(cfgEl.textContent);

    var cutInputs = Array.prototype.slice.call(root.querySelectorAll('[data-hsb-cut]'));
    var fabricInputs = Array.prototype.slice.call(root.querySelectorAll('[data-hsb-fabric]'));
    var hwsetInputs = Array.prototype.slice.call(root.querySelectorAll('[data-hsb-hwset]'));
    var addonInputs = Array.prototype.slice.call(root.querySelectorAll('[data-hsc-addon]'));

    var fitWrap = root.querySelector('[data-hsb-fit]');
    var sizeSel = root.querySelector('[data-hsb-size]');
    var waistSel = root.querySelector('[data-hsb-waist-adjust]');
    var inseamSel = root.querySelector('[data-hsb-inseam]');
    var modeSizeWrap = root.querySelector('[data-hsb-mode-size]');
    var modeMeasureWrap = root.querySelector('[data-hsb-mode-measure]');
    var mWaist = root.querySelector('[data-hsb-measure-waist]');
    var mInseam = root.querySelector('[data-hsb-measure-inseam]');
    var mResult = root.querySelector('[data-hsb-measure-result]');

    var addBtn = root.querySelector('[data-hsb-add]');
    var errEl = root.querySelector('[data-hsb-error]');
    var photo = root.querySelector('[data-hsb-photo]');

    function selectedCut() {
      var el = cutInputs.filter(function (i) { return i.checked; })[0];
      if (!el) return null;
      return { el: el, config: JSON.parse(el.getAttribute('data-config')) };
    }
    function selectedFabric() {
      return fabricInputs.filter(function (i) { return i.checked; })[0] || null;
    }
    function selectedHwset() {
      var el = hwsetInputs.filter(function (i) { return i.checked; })[0];
      return el && el.value ? el : null;
    }
    function selectedAddons() {
      return addonInputs.filter(function (i) { return i.checked; });
    }

    function fillSelect(sel, values, fmt) {
      if (!sel) return;
      sel.innerHTML = '';
      (values || []).forEach(function (v) {
        var opt = document.createElement('option');
        opt.value = v;
        opt.textContent = fmt ? fmt(v) : v;
        sel.appendChild(opt);
      });
    }

    // The fit step is jean-shaped: waist size, waist adjustment, inseam, and a
    // measurement mode that maps numbers onto a grade. A tote has none of that -
    // it grades S/M/L with no waist and no inseam. Read the config instead of
    // assuming, or the tote renders "WS/WM/WL", empty waist and inseam
    // dropdowns, and a measurement mode that parseFloats "S" into NaN.
    function isOneSize(c) { return !(c && c.sizes && c.sizes.length); }
    function hasWaist(c) { return !!(c && c.waist_adjust && c.waist_adjust.length); }
    function hasInseam(c) { return !!(c && c.inseams && c.inseams.length); }
    // "W32" is a waist. "WS" is nonsense. Only prefix when the size is a number.
    function sizeLabel(c, v) {
      return (c && c.size_label) || isNaN(parseFloat(v)) ? String(v) : 'W' + v;
    }
    // hide a select together with the sublabel that introduces it
    function setFieldShown(sel, shown) {
      if (!sel) return;
      sel.hidden = !shown;
      var lab = sel.previousElementSibling;
      if (lab && lab.classList && lab.classList.contains('hsc__sublabel')) lab.hidden = !shown;
    }

    function onCutChange() {
      var cut = selectedCut();
      if (!cut) { if (fitWrap) fitWrap.hidden = true; return; }
      var c = cut.config;
      if (fitWrap) fitWrap.hidden = isOneSize(c);
      if (isOneSize(c)) { updateSummary(); return; }
      // measurement mode maps a waist number onto the grade - meaningless without one
      var modes = root.querySelector('.hsb__modes');
      if (modes) modes.hidden = !hasWaist(c);
      if (!hasWaist(c)) {
        var sizeMode = root.querySelector('input[name^="fitmode-"][value="size"]');
        if (sizeMode && !sizeMode.checked) { sizeMode.checked = true; sizeMode.dispatchEvent(new Event('change', { bubbles: true })); }
      }
      setFieldShown(waistSel, hasWaist(c));
      setFieldShown(inseamSel, hasInseam(c));
      fillSelect(sizeSel, c.sizes, function (v) { return sizeLabel(c, v); });
      fillSelect(waistSel, c.waist_adjust, function (v) { return v === '0' ? 'NONE (TRUE TO PATTERN)' : v + '"'; });
      if (waistSel) waistSel.value = '0';
      fillSelect(inseamSel, c.inseams, function (v) { return v + '"'; });
      mapMeasurements();
      updateSummary();
    }

    // Measurement mode: nearest graded size + bounded adjustment, or refuse.
    function mapMeasurements() {
      if (!mWaist || !mResult) return null;
      var cut = selectedCut();
      if (!cut) return null;
      var c = cut.config;
      var w = parseFloat(mWaist.value);
      var ins = parseFloat(mInseam.value);
      if (isNaN(w) || isNaN(ins)) {
        mResult.textContent = 'ENTER BOTH NUMBERS — WE MAP TO THE NEAREST GRADED SIZE + BOUNDED ADJUSTMENT.';
        return null;
      }
      var best = null;
      (c.sizes || []).forEach(function (s) {
        (c.waist_adjust || []).forEach(function (adj) {
          var candidate = parseFloat(s) + parseFloat(adj);
          var diff = Math.abs(candidate - w);
          if (!best || diff < best.diff) best = { size: s, adjust: adj, diff: diff };
        });
      });
      var inseamOk = (c.inseams || []).map(parseFloat);
      var nearestInseam = null, insDiff = Infinity;
      inseamOk.forEach(function (v) {
        var d = Math.abs(v - ins);
        if (d < insDiff) { insDiff = d; nearestInseam = v; }
      });
      // Refuse anything beyond what a bounded adjustment can reach.
      if (!best || best.diff > 0.5 || nearestInseam === null || insDiff > 1.5) {
        mResult.textContent = 'OUT OF THE VALIDATED RANGE FOR THIS CUT. EMAIL US — WE DO NOT GUESS PATTERNS.';
        return null;
      }
      var mapped = {
        size: best.size,
        adjust: best.adjust,
        inseam: String(nearestInseam),
        fromMeasurements: w + '" WAIST / ' + ins + '" INSEAM'
      };
      mResult.textContent = 'MAPPED: SIZE W' + mapped.size +
        (mapped.adjust !== '0' ? ' ' + mapped.adjust + '" WAIST ADJUST' : ' (TRUE TO PATTERN)') +
        ', ' + mapped.inseam + '" INSEAM.';
      return mapped;
    }

    function currentFit() {
      var oneSizeCut = selectedCut();
      if (oneSizeCut && isOneSize(oneSizeCut.config)) {
        return { size: 'ONE SIZE', adjust: '0', inseam: '', source: 'ONE SIZE' };
      }
      var mode = root.querySelector('input[name^="fitmode-"]:checked');
      if (mode && mode.value === 'measure') {
        var mapped = mapMeasurements();
        return mapped ? {
          size: mapped.size, adjust: mapped.adjust, inseam: mapped.inseam,
          source: 'MEASUREMENTS (' + mapped.fromMeasurements + ')'
        } : null;
      }
      if (!sizeSel || !sizeSel.value) return null;
      // Do not report a waist adjustment or an inseam for a garment that has
      // neither - it would print an empty ticket field the floor has to guess at.
      var cfg = oneSizeCut ? oneSizeCut.config : null;
      return {
        size: sizeSel.value,
        adjust: hasWaist(cfg) && waistSel ? waistSel.value : '0',
        inseam: hasInseam(cfg) && inseamSel ? inseamSel.value : '',
        source: 'SIZE PICKER'
      };
    }

    var sumCut = root.querySelector('[data-hsb-sum-cut]');
    var sumFabric = root.querySelector('[data-hsb-sum-fabric]');
    var sumAddons = root.querySelector('[data-hsb-sum-addons]');
    var sumTotal = root.querySelector('[data-hsb-sum-total]');
    var sumLead = root.querySelector('[data-hsb-sum-lead]');

    function updateSummary() {
      var cut = selectedCut();
      var fabric = selectedFabric();
      var hwset = selectedHwset();
      var addons = selectedAddons();

      var cutCents = cut ? parseInt(cut.el.getAttribute('data-price'), 10) : 0;
      var fabricCents = fabric ? parseInt(fabric.getAttribute('data-price'), 10) : 0;
      var extraCents = (hwset ? parseInt(hwset.getAttribute('data-price'), 10) : 0) +
        addons.reduce(function (s, i) { return s + (parseInt(i.getAttribute('data-price'), 10) || 0); }, 0);

      var lead = (cut ? (cut.config.lead_days || 21) : 0) +
        (fabric ? (parseInt(fabric.getAttribute('data-lead'), 10) || 0) : 0) +
        Math.max(
          hwset ? (parseInt(hwset.getAttribute('data-lead'), 10) || 0) : 0,
          addons.reduce(function (m, i) { return Math.max(m, parseInt(i.getAttribute('data-lead'), 10) || 0); }, 0)
        );

      if (sumCut) sumCut.textContent = cut ? money(cutCents, cfg.moneyFormat) : '—';
      if (sumFabric) sumFabric.textContent = money(fabricCents, cfg.moneyFormat);
      if (sumAddons) sumAddons.textContent = money(extraCents, cfg.moneyFormat);
      if (sumTotal) sumTotal.textContent = money(cutCents + fabricCents + extraCents, cfg.moneyFormat);
      if (sumLead) sumLead.textContent = lead + ' BUSINESS DAYS';
      return lead;
    }

    cutInputs.forEach(function (i) { i.addEventListener('change', onCutChange); });
    fabricInputs.forEach(function (i) {
      i.addEventListener('change', function () {
        var src = i.getAttribute('data-photo');
        if (src && photo && photo.tagName === 'IMG') photo.src = src;
        updateSummary();
      });
    });
    hwsetInputs.forEach(function (i) { i.addEventListener('change', updateSummary); });
    addonInputs.forEach(function (i) {
      i.addEventListener('change', function () {
        var note = root.querySelector('[data-hsc-addon-note="' + i.value + '"]');
        if (note) note.hidden = !i.checked;
        updateSummary();
      });
    });
    // hs-customizer.js owns placement on this page and can tick an add-on by
    // itself — an automatic surcharge, e.g. removing and replacing a back pocket
    // so chainstitch can go on it. That flip is not a user event, and this file's
    // change listener is registered first anyway, so without this hook the price
    // panel would lag one interaction behind the cart. Counterpart to the
    // root.hsPlacementFor bridge that file exposes for the add-to-cart guard.
    root.hsRefreshSummary = updateSummary;
    Array.prototype.forEach.call(root.querySelectorAll('input[name^="fitmode-"]'), function (m) {
      m.addEventListener('change', function () {
        var checked = root.querySelector('input[name^="fitmode-"]:checked');
        var mode = checked ? checked.value : 'size';
        if (modeSizeWrap) modeSizeWrap.hidden = mode !== 'size';
        if (modeMeasureWrap) modeMeasureWrap.hidden = mode !== 'measure';
        updateSummary();
      });
    });
    [mWaist, mInseam].forEach(function (el) { if (el) el.addEventListener('input', function () { mapMeasurements(); }); });
    [sizeSel, waistSel, inseamSel].forEach(function (el) { if (el) el.addEventListener('change', updateSummary); });

    onCutChange();

    function showError(msg) {
      if (!errEl) return;
      errEl.textContent = msg;
      errEl.hidden = false;
      // Every gate rejection funnels through here, so this one line measures all
      // of them. Which gate turns the most people away is the question the
      // checklist actually asked - "which step loses people" - and no
      // page-level analytics can see it, because nothing navigates.
      if (track) track.event('hs_build_blocked', { reason: msg });
    }
    // Clear the TEXT as well as the flag. Hiding alone leaves a stale sentence in
    // the DOM that a screen reader still reaches and that reappears the instant
    // anything unhides the region. Bound to change/input below so the complaint
    // dies when the customer fixes the thing it complains about — otherwise the
    // page keeps saying TAP THE JEAN TO PLACE after freestyle has been ticked,
    // and it survives all the way into the ADDED ✓ state.
    function clearError() {
      if (!errEl) return;
      errEl.textContent = '';
      errEl.hidden = true;
    }

    // --- Placement markers + live build sheet ------------------------------
    function zoneState(zone) {
      if (zone === 'FABRIC') {
        var f = selectedFabric();
        return { label: 'FABRIC', value: f ? f.getAttribute('data-title') : 'PICK ONE', custom: !!f };
      }
      if (zone === 'HARDWARE') {
        var h = selectedHwset();
        return { label: 'HARDWARE', value: h ? h.getAttribute('data-title') : 'STOCK — AS PATTERNED', custom: !!h };
      }
      if (zone === 'ADDONS') {
        var n = selectedAddons().length;
        return { label: 'CUSTOM WORK', value: n ? n + ' SELECTED' : 'NONE', custom: n > 0 };
      }
      var g = root.querySelector('[data-hsb-thread-placement="' + zone + '"]');
      if (!g) return null;
      var c = g.querySelector('input:checked');
      var v = c ? c.value : 'STOCK — TONAL';
      return { label: zone + ' THREAD', value: v, custom: v.indexOf('STOCK') !== 0 };
    }

    var markers = Array.prototype.slice.call(root.querySelectorAll('[data-hsb-marker]'));
    var specWrap = root.querySelector('[data-hsb-spec]');
    var specRows = root.querySelector('[data-hsb-spec-rows]');

    function updateMarkers() {
      var anyCustom = false;
      var rowsHtml = '';
      var cut = selectedCut();
      if (cut) {
        rowsHtml += '<div class="hsc__spec-row is-custom"><span>CUT</span><span>' + cut.el.getAttribute('data-title') + '</span></div>';
        var fit = currentFit();
        if (fit) {
          // Same rule as the cart properties: never show a waist adjustment or an
          // inseam for a garment that has neither, or a tote reads "WM / " INSEAM".
          var fitTxt = sizeLabel(cut.config, fit.size);
          if (hasWaist(cut.config) && fit.adjust !== '0') fitTxt += ' ' + fit.adjust + '&quot;';
          if (hasInseam(cut.config) && fit.inseam) fitTxt += ' / ' + fit.inseam + '&quot; INSEAM';
          rowsHtml += '<div class="hsc__spec-row"><span>FIT</span><span>' + fitTxt + '</span></div>';
        }
        anyCustom = true;
      }
      markers.forEach(function (mk) {
        var zone = mk.getAttribute('data-zone');
        var st = zoneState(zone);
        if (!st) { mk.hidden = true; return; }
        mk.hidden = false;
        mk.textContent = st.custom ? st.label + ': ' + st.value : st.label;
        mk.classList.toggle('is-custom', st.custom);
        if (st.custom) anyCustom = true;
        rowsHtml += '<div class="hsc__spec-row' + (st.custom ? ' is-custom' : '') + '"><span>' + st.label + '</span><span>' + st.value + '</span></div>';
      });
      if (specRows) specRows.innerHTML = rowsHtml;
      if (specWrap) specWrap.hidden = !anyCustom;
    }

    markers.forEach(function (mk) {
      mk.addEventListener('click', function () {
        var zone = mk.getAttribute('data-zone');
        var target =
          zone === 'FABRIC' ? root.querySelector('[data-hsb-fabric]') :
          zone === 'HARDWARE' ? root.querySelector('[data-hsb-hwset]') :
          zone === 'ADDONS' ? root.querySelector('[data-hsc-addon]') :
          root.querySelector('[data-hsb-thread-placement="' + zone + '"]');
        if (!target) return;
        var group = target.closest('.hsc__group, .hsc__step') || target;
        group.scrollIntoView({ behavior: 'smooth', block: 'center' });
        group.classList.add('is-flash');
        setTimeout(function () { group.classList.remove('is-flash'); }, 1600);
      });
    });

    root.addEventListener('change', updateMarkers);
    updateMarkers();

    // --- SIZE GUIDE + MADE-TO-MEASURE GATE ---------------------------------
    // Both render from the cut's own cut_config: chart_cols, chart, graded and
    // made_to_measure. Nothing is hardcoded here, so the owner edits the chart
    // in Admin and this follows. A cut with no chart simply hides the guide.
    var guide = root.querySelector('[data-hsb-guide]');
    var guideBody = root.querySelector('[data-hsb-guide-body]');
    var guideToggle = root.querySelector('[data-hsb-guide-toggle]');
    var guideTable = root.querySelector('[data-hsb-guide-table]');
    var guideUnit = root.querySelector('[data-hsb-guide-unit]');
    var mtm = root.querySelector('[data-hsb-mtm]');
    var mtmAck = root.querySelector('[data-hsb-mtm-ack]');
    var mtmSize = root.querySelector('[data-hsb-mtm-size]');
    var mtmGraded = root.querySelector('[data-hsb-mtm-graded]');

    var LABEL = {waist:'WAIST', seat:'SEAT', thigh:'THIGH', knee:'KNEE', hem:'HEM',
                 front_rise:'F. RISE', back_rise:'B. RISE'};

    function esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }
    function isMTM(cfg, size) {
      var list = cfg && cfg.made_to_measure;
      if (!list || !list.length) return false;
      for (var i = 0; i < list.length; i++) {
        if (String(list[i]) === String(size)) return true;
      }
      return false;
    }

    if (guideToggle) {
      guideToggle.addEventListener('click', function () {
        var open = guideToggle.getAttribute('aria-expanded') === 'true';
        guideToggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (guideBody) guideBody.hidden = open;
      });
    }

    function renderGuide() {
      var cut = selectedCut();
      var cfg = cut ? cut.config : null;
      var cols = cfg && cfg.chart_cols;
      var chart = cfg && cfg.chart;
      if (!guide) return;
      if (!cfg || !cols || !chart) { guide.hidden = true; return; }
      guide.hidden = false;
      if (guideUnit) guideUnit.textContent = cfg.chart_unit || '';

      var current = sizeSel ? sizeSel.value : null;
      var head = '<thead><tr><th>SIZE</th>';
      cols.forEach(function (c) {
        if (c === 'waist') return;                 // waist is the row label
        head += '<th>' + esc(LABEL[c] || c.toUpperCase()) + '</th>';
      });
      head += '</tr></thead>';

      var body = '<tbody>';
      (cfg.sizes || []).forEach(function (s) {
        var row = chart[s];
        if (!row) return;                          // size offered but no chart cell
        body += '<tr data-current="' + (String(s) === String(current)) + '"'
              + ' data-mtm="' + isMTM(cfg, s) + '"><td>' + esc(s) + '</td>';
        cols.forEach(function (c, i) {
          if (c === 'waist') return;
          var v = row[i];
          body += '<td>' + (v == null ? '&mdash;' : esc(v)) + '</td>';
        });
        body += '</tr>';
      });
      body += '</tbody>';
      if (guideTable) guideTable.innerHTML = head + body;
    }

    function renderMTM() {
      if (!mtm) return;
      var cut = selectedCut();
      var cfg = cut ? cut.config : null;
      var size = sizeSel ? sizeSel.value : null;
      var on = !!(cfg && size && isMTM(cfg, size));
      if (!on) {
        mtm.hidden = true;
        if (mtmAck) mtmAck.checked = false;        // never carry an ack across sizes
        return;
      }
      mtm.hidden = false;
      if (mtmSize) mtmSize.textContent = size;
      if (mtmGraded && cfg.graded) mtmGraded.textContent = cfg.graded;
    }

    // --- LIVE MEASUREMENTS --------------------------------------------------
    // Choosing a cut used to change nothing a customer could see. The traced flat
    // is one drawing for every cut on purpose — its 34 zones are the factory
    // contract, and distorting them per silhouette would break placement against
    // the ticket — and the size guide answers a different question, because it is
    // collapsed and lists every size at once. Neither answers "what actually
    // changes if I pick WIDE instead of SLIM".
    //
    // So the cut is made legible in numbers. Every value is the owner's own graded
    // chart row for the selected cut and size; nothing here is computed, estimated
    // or interpolated, and a cut with no chart simply shows nothing rather than
    // inventing a figure for a garment we have not graded.
    var measure = root.querySelector('[data-hsb-measure]');
    var lastMeasure = null;

    function renderMeasure() {
      if (!measure) return;
      var cut = selectedCut();
      var cfg = cut ? cut.config : null;
      var cols = cfg && cfg.chart_cols;
      var chart = cfg && cfg.chart;
      var size = sizeSel ? sizeSel.value : null;
      var row = (cols && chart && size) ? chart[size] : null;
      if (!row) { measure.hidden = true; lastMeasure = null; return; }

      var seen = {};
      var cells = '';
      cols.forEach(function (c, i) {
        if (c === 'waist') return;                 // the row label, not a measurement
        var v = row[i];
        var shown = (v == null) ? '&mdash;' : esc(v) + '&quot;';
        seen[c] = shown;
        // Marking only what MOVED is the whole point — it puts the eye on the
        // measurement the new cut actually changed, which is the question being
        // asked. On the first render there is nothing to compare against.
        var moved = lastMeasure && lastMeasure[c] !== shown;
        cells += '<div class="hsb__m' + (moved ? ' is-moved' : '') + '">'
              // LABEL only names the jean columns. A tote charts width, height,
              // gusset and strap_drop, and a future garment will chart something
              // else again — so an unnamed column is titled from its own key
              // rather than printing STRAP_DROP at a customer.
              +  '<span class="hsb__m-k">' + esc(LABEL[c] || c.toUpperCase().replace(/_/g, ' ')) + '</span>'
              +  '<span class="hsb__m-v">' + shown + '</span></div>';
      });
      lastMeasure = seen;

      measure.innerHTML =
        '<p class="hsb__m-title">THIS CUT, THIS SIZE — FINISHED GARMENT</p>'
        + '<div class="hsb__m-grid">' + cells + '</div>'
        + (cfg.chart_unit ? '<p class="hsb__m-unit">' + esc(cfg.chart_unit) + '</p>' : '');
      measure.hidden = false;
    }

    function refreshFitPanels() { renderGuide(); renderMTM(); renderMeasure(); }
    root.addEventListener('change', refreshFitPanels);
    refreshFitPanels();

    // --- URL PREFILL + SHAREABLE BUILD -------------------------------------
    // A combo on the parlor board is just a LINK to this page with the choices
    // already in the query string, so the board is theme-editor content and
    // never a custom section. The same code gives refresh-persistence and a
    // shareable build URL.
    //
    //   ?cut=WIDE&fabric=DEADSTOCK&hw=BRASS&add=CHAINSTITCH,STUDS&size=32&inseam=32
    //
    // Tokens are matched loosely against variant id, then SKU, then title, so a
    // link can be written by hand in Admin without looking up any ids.
    function slugify(s) {
      return String(s == null ? '' : s).toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+/, '').replace(/-+$/, '');
    }
    function matchInput(inputs, token) {
      var t = slugify(token);
      if (!t) return null;
      var by = function (get) {
        return inputs.filter(function (i) {
          var v = slugify(get(i));
          return v && v === t;
        })[0] || null;
      };
      return by(function (i) { return i.value; })
        || by(function (i) { return i.getAttribute('data-sku'); })
        || by(function (i) { return i.getAttribute('data-title'); });
    }
    // Fire a real bubbling change so every listener above runs, including the
    // delegated updateMarkers on root. Setting .checked alone fires nothing.
    function fire(el) {
      if (el) el.dispatchEvent(new Event('change', { bubbles: true }));
    }

    var PARAMS = ['cut', 'fabric', 'hw', 'add', 'size', 'waist', 'inseam'];

    function readQuery() {
      try { return new URLSearchParams(window.location.search); } catch (e) { return null; }
    }

    function applyUrlPrefill() {
      var q = readQuery();
      if (!q) return;

      // Cut first: onCutChange repopulates the size/waist/inseam selects, so
      // setting a size before the cut is applied would be overwritten.
      var cut = matchInput(cutInputs, q.get('cut'));
      if (cut) { cut.checked = true; fire(cut); }

      var fab = matchInput(fabricInputs, q.get('fabric'));
      if (fab) { fab.checked = true; fire(fab); }

      var hw = matchInput(hwsetInputs, q.get('hw'));
      if (hw) { hw.checked = true; fire(hw); }

      var wanted = (q.get('add') || '').split(',');
      wanted.forEach(function (tok) {
        var a = matchInput(addonInputs, tok);
        if (a && !a.checked) { a.checked = true; fire(a); }
      });

      // Selects are populated by now. Only accept a value the cut actually offers.
      [[sizeSel, 'size'], [waistSel, 'waist'], [inseamSel, 'inseam']].forEach(function (pair) {
        var sel = pair[0], raw = q.get(pair[1]);
        if (!sel || raw == null) return;
        var want = slugify(raw);
        var opt = Array.prototype.filter.call(sel.options, function (o) {
          return slugify(o.value) === want;
        })[0];
        if (opt) { sel.value = opt.value; fire(sel); }
      });
    }

    // Write the current build back to the URL. Starts from the EXISTING query
    // so preview_theme_id and any utm_* survive - rebuilding it from scratch
    // would drop preview_theme_id and silently bounce the viewer onto the live
    // theme, which is the trap documented in the master brief.
    function syncUrl() {
      if (!window.history || !window.history.replaceState) return;
      var q = readQuery();
      if (!q) return;
      PARAMS.forEach(function (k) { q.delete(k); });

      var cut = selectedCut();
      if (cut) q.set('cut', cut.el.getAttribute('data-sku') || cut.el.value);
      var fab = selectedFabric();
      if (fab) q.set('fabric', slugify(fab.getAttribute('data-title')) || fab.value);
      var hw = selectedHwset();
      if (hw) q.set('hw', slugify(hw.getAttribute('data-title')) || hw.value);
      var add = selectedAddons().map(function (a) {
        return slugify(a.getAttribute('data-title')) || a.value;
      });
      if (add.length) q.set('add', add.join(','));
      if (sizeSel && sizeSel.value) q.set('size', sizeSel.value);
      if (waistSel && waistSel.value && waistSel.value !== '0') q.set('waist', waistSel.value);
      if (inseamSel && inseamSel.value) q.set('inseam', inseamSel.value);

      var qs = q.toString();
      try {
        window.history.replaceState(null, '', window.location.pathname
          + (qs ? '?' + qs : '') + window.location.hash);
      } catch (e) { /* non-fatal: the build still works, it just is not shareable */ }
    }

    // --- Instrumentation ----------------------------------------------------
    // One delegated listener rather than a call inside every handler: the funnel
    // should not be something you can forget to update when a control is added.
    // Absent hs-track.js this is null and every call site short-circuits.
    var track = window.HSTrack ? window.HSTrack.for('build') : null;
    if (track) {
      track.step('start');
      root.addEventListener('change', function (e) {
        var t = e.target;
        if (!t || !t.hasAttribute) return;
        if (t.hasAttribute('data-hsb-cut')) {
          track.step('garment', { cut: t.getAttribute('data-title') || '' });
        } else if (t.hasAttribute('data-hsb-fabric')) {
          track.step('fabric', { fabric: t.getAttribute('data-title') || '' });
        } else if (t.hasAttribute('data-hsc-addon')) {
          track.step('work');
        } else if (t.hasAttribute('data-hsb-size') || t.hasAttribute('data-hsb-waist-adjust') ||
                   t.hasAttribute('data-hsb-inseam')) {
          track.step('fit');
        }
      });
      // Typed measurements are an input, not a change, and they are the OTHER
      // way to reach step 3 - a funnel that only counted the dropdowns would
      // report made-to-measure customers as never having given us a fit.
      root.addEventListener('input', function (e) {
        var t = e.target;
        if (!t || !t.hasAttribute) return;
        if (t.hasAttribute('data-hsb-measure-waist') || t.hasAttribute('data-hsb-measure-inseam')) {
          track.step('fit', { source: 'measured' });
        }
      });
    }

    applyUrlPrefill();
    root.addEventListener('change', syncUrl);
    // A correction is a change or a keystroke. Both have to retire the message,
    // or the customer fixes the fault and is still being told off for it.
    root.addEventListener('change', clearError);
    root.addEventListener('input', clearError);
    syncUrl();

    addBtn && addBtn.addEventListener('click', function () {
      clearError();

      var cut = selectedCut();
      if (!cut) { showError('PICK A CUT.'); return; }
      var fit = currentFit();
      if (!fit) { showError('FIT IS OUT OF THE VALIDATED RANGE OR INCOMPLETE. FIX STEP 02.'); return; }

      // Made-to-measure sizes have no pattern column. Refuse until acknowledged.
      var mtmOn = mtm && !mtm.hidden;
      if (mtmOn && (!mtmAck || !mtmAck.checked)) {
        showError('SIZE ' + (sizeSel ? sizeSel.value : '') + ' IS MADE TO MEASURE. TICK THE BOX IN STEP 02 TO CONFIRM WE MAY CONTACT YOU BEFORE CUTTING.');
        if (mtm.scrollIntoView) mtm.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      var fabric = selectedFabric();
      if (fabricInputs.length && !fabric) { showError('PICK A FABRIC.'); return; }

      // Same gate the CUSTOMIZE flow enforces. hs-customizer.js runs here in
      // placement-only mode, so its own add-to-cart guard never fires — without
      // this a $2,000 build could ship a placeable technique with no marks at
      // all, and the factory ticket would say what to sew but not where.
      // FREESTYLE satisfies the gate; it is a deliberate choice, not a blank.
      var unplaced = selectedAddons().filter(function (input) {
        var raw = input.getAttribute('data-placement');
        if (!raw || raw === 'null') return false;      // no rules configured
        if (typeof root.hsPlacementFor !== 'function') return false;
        var pl = root.hsPlacementFor(input.value);
        return !pl.freestyle && !pl.zones.length;
      });
      if (unplaced.length) {
        showError('TAP THE JEAN TO PLACE: ' + unplaced.map(function (i) {
          return i.getAttribute('data-title');
        }).join(', ') + ' — OR TICK FREESTYLE AND WE WILL CHOOSE.');
        var flat = root.querySelector('[data-hsc-flat]');
        if (flat) {
          flat.scrollIntoView({ behavior: 'smooth', block: 'center' });
          flat.classList.add('is-flash');
          setTimeout(function () { flat.classList.remove('is-flash'); }, 1600);
        }
        return;
      }

      // Fired AFTER every gate has passed, so submit means "we sent it", not
      // "they pressed a button". A validation bounce is its own event below -
      // conflating them would hide the most useful number on the page: how many
      // people the gates turn away.
      track && track.step('submit');

      addBtn.disabled = true;
      addBtn.textContent = 'BUILDING…';

      var lead = updateSummary();
      var ref = 'BUILD-' + Date.now().toString(36).toUpperCase();
      var entityU = cfg.entity.toUpperCase();
      var items = [];

      var baseProps = {
        'ENTITY': entityU,
        'ORDER TYPE': 'CUSTOM BUILD',
        'CUSTOMIZATION REF': ref,
        'PATTERN REF': cut.config.pattern_ref || cut.el.getAttribute('data-sku'),
        // Factory-facing. A made-to-measure size has no pattern column to pull.
        'MADE TO MEASURE': mtmOn ? 'YES — DRAFT AND CONFIRM BEFORE CUTTING' : 'NO',
        'BASE SIZE': sizeLabel(cut.config, fit.size),
        'FIT SOURCE': fit.source,
        'ESTIMATED LEAD TIME': lead + ' BUSINESS DAYS'
      };
      // Only state a waist adjustment or an inseam when the garment HAS one. The
      // tote was printing BASE SIZE "WM", WAIST ADJUST "NONE" and INSEAM '"' - a
      // bare inch mark - which is three lines of nonsense for the floor to read
      // on a bag. An absent field is clearer than a filled-in meaningless one.
      if (hasWaist(cut.config)) {
        baseProps['WAIST ADJUST'] = fit.adjust === '0' ? 'NONE' : fit.adjust + '"';
      }
      if (hasInseam(cut.config) && fit.inseam) {
        baseProps['INSEAM'] = fit.inseam + '"';
      }
      Array.prototype.forEach.call(root.querySelectorAll('[data-hsb-thread-placement]'), function (group) {
        var checked = group.querySelector('input:checked');
        if (checked) baseProps['THREAD — ' + group.getAttribute('data-hsb-thread-placement')] = checked.value;
      });
      items.push({ id: parseInt(cut.el.value, 10), quantity: 1, properties: baseProps });

      if (fabric) {
        items.push({
          id: parseInt(fabric.value, 10), quantity: 1,
          properties: {
            'ENTITY': entityU, 'ORDER TYPE': 'CUSTOM BUILD FABRIC',
            'CUSTOMIZATION REF': ref, 'APPLIED TO': cut.el.getAttribute('data-title')
          }
        });
      }

      var hwset = selectedHwset();
      if (hwset) {
        items.push({
          id: parseInt(hwset.value, 10), quantity: 1,
          properties: {
            'ENTITY': entityU, 'ORDER TYPE': 'CUSTOMIZATION ADD-ON',
            'CUSTOMIZATION REF': ref, 'APPLIED TO': cut.el.getAttribute('data-title')
          }
        });
      }

      selectedAddons().forEach(function (input) {
        var note = root.querySelector('[data-hsc-addon-note="' + input.value + '"]');
        var props = {
          'ENTITY': entityU, 'ORDER TYPE': 'CUSTOMIZATION ADD-ON',
          'CUSTOMIZATION REF': ref, 'APPLIED TO': cut.el.getAttribute('data-title')
        };
        if (note && note.value.trim()) props['NOTES'] = note.value.trim().toUpperCase();

        // Placement comes from the shared picker in hs-customizer.js, which runs
        // here in placement-only mode and publishes root.hsPlacementFor. Without
        // this the customer could place marks that never reached the factory.
        if (typeof root.hsPlacementFor === 'function') {
          var pl = root.hsPlacementFor(input.value);
          if (pl.freestyle) {
            props['PLACEMENT'] = 'FREESTYLE — SHOP CHOOSES';
            props['_PLACEMENT_ZONES'] = 'FREESTYLE';
          } else if (pl.zones.length) {
            props['PLACEMENT'] = pl.zones.map(function (z) {
              var el = root.querySelector('[data-hsf-zone="' + z + '"]');
              return el ? el.getAttribute('data-zone-label') : z;
            }).join(' + ');
            props['_PLACEMENT_ZONES'] = pl.zones.join(',');
            props['_PLACEMENT_XY'] = pl.xy;
          }
        }
        items.push({ id: parseInt(input.value, 10), quantity: 1, properties: props });
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || j.message || 'CART ERROR'); });
          return r.json();
        })
        .then(function () {
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { entity: cfg.entity } })
          });
        })
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          // Stay put unless a redirect is explicitly configured. Navigating to
          // /cart 302s and drops ?preview_theme_id, dumping the customer on the
          // live theme's cart so the build looks like it vanished. The tray below
          // shows what was added and lets them remove it. Same rule as CUSTOMIZE.
          if (cfg.redirect) { window.location.href = cfg.redirect; return; }
          document.dispatchEvent(new CustomEvent('hsdb:cart-changed', { bubbles: true }));
          if (track) {
            track.step('added');
            // Value is summed from the same cents the cart was sent, not from a
            // formatted string - a yen display carries no decimals and parsing
            // it back would silently divide the number by a hundred.
            var cents = 0;
            for (var vi = 0; vi < items.length; vi++) {
              var el = root.querySelector('[value="' + items[vi].id + '"]');
              cents += el ? (parseInt(el.getAttribute('data-price'), 10) || 0) : 0;
            }
            track.event('hs_add_to_cart', {
              value: cents / 100, ref: ref, lines: items.length,
              cut: cut.el.getAttribute('data-title') || '', lead_days: lead
            });
          }
          clearError();
          addBtn.disabled = false;
          addBtn.textContent = 'ADDED ✓ — YOUR BUILD IS IN THE BAG';
          var tray = document.querySelector('[data-hsdb-tray]');
          if (tray) tray.scrollIntoView({ behavior: 'smooth', block: 'center' });
        })
        .catch(function (e) {
          showError((e.message || 'SOMETHING BROKE. TRY AGAIN.').toUpperCase());
          addBtn.disabled = false;
          addBtn.textContent = 'BUILD IT';
        });
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('.hsb'), initSection);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector('.hsb');
    if (el) initSection(el);
  });
})();
